import Icon from '@atoms/Icon/Icon';
import { ComponentProps, ElementType } from 'react';
import { twMerge } from 'tailwind-merge';

const defaultElement = 'button';

export const enum TagVariant {
  Grey = 'grey',
  Yellow = 'yellow',
}

type ExplicitProps = {
  label: string;
  isActive?: boolean;
  largeText?: boolean;
  icon?: boolean;
  variant?: TagVariant;
};

type PropsWithAs<TElementType extends ElementType = ElementType> =
  ExplicitProps & {
    as?: TElementType;
  };

type ITag<TElementType extends ElementType> = PropsWithAs<TElementType> &
  Omit<ComponentProps<TElementType>, keyof PropsWithAs>;

function Tag<TElementType extends ElementType = typeof defaultElement>({
  as,
  label,
  isActive,
  largeText,
  icon = false,
  variant = TagVariant.Grey,
  ...otherProps
}: ITag<TElementType>) {
  const Wrapper = as ?? 'button';
  const isClickable = Wrapper === 'button' || Wrapper === 'a';
  const hoverStyles =
    variant === TagVariant.Yellow
      ? "hover:tw-bg-yellow-hover data-[active='true']:tw-border-yellow data-[active='true']:tw-bg-yellow hover:tw-border-yellow-hover"
      : "hover:tw-bg-grey-30 data-[active='true']:tw-border-grey-40 data-[active='true']:tw-bg-grey-40";

  return (
    <Wrapper
      {...otherProps}
      className={twMerge(
        otherProps?.className,
        icon ? 'tw-items-center tw-justify-between tw-gap-2' : '',
        largeText ? 'tw-py-0.5 tw-text-sm' : 'tw-py-[3px] tw-text-xs',
        'tw-flex tw-rounded tw-border tw-border-grey-60 tw-px-2 tw-text-grey-70',
        isClickable
          ? `${hoverStyles} hover:tw-text-grey-100 data-[active='true']:tw-text-grey-100`
          : 'tw-pointer-events-none'
      )}
      data-active={isActive}
    >
      {label}
      {icon && <Icon name="cross" size="1rem" />}
    </Wrapper>
  );
}

export default Tag;
